import React, { JSX } from "react";
import { Box, Typography } from "@mui/material";
import {
  scaleFont,
  verifyShowTestCategory,
  createMarkup,
  sortCategoryKeys,
  advancedLabRangeIndicator,
  AdvancedLabRangeHandler,
  AdvancedRange,
  showCategory,
  showSubRow,
  addDefaultFormData,
  LabHeadersFlexBasisLargeFont,
  readingPresent,
  isRadiologyOrCytology
} from "../../LabPrintFunctions";
import styles from "../../../Lab.module.css";
import { LabTest, LabTestRecordResultData } from "../../../../../interfaces/Lab";
import { EditableTableView } from "../../../../../components/EditableTable/EditableTable";
import hasOwnProperty from "../../../../../helpers/object";
import { removeCommasFromNumber } from "../../../../../helpers/number";
import { LabTestStatusTypes } from "../../../LabList";
import { TestNameWithSampleInfo } from "../DefaultTemplate/DefaultTemplateCore";
import { useShowLabTestsWithoutReadingsInPrint } from "../../../../../hooks/useShowLabTestsWithoutReadingsInPrint";

const EXCLUDE_GROUP_CATEGORY = ["Special", "undefined"]; // add group category here to hide

const CustomEl = ({ data }) => (
  <div
    // eslint-disable-next-line react/no-danger
    dangerouslySetInnerHTML={createMarkup(data)}
    style={{ whiteSpace: "pre-wrap" }}
    className={styles.additionalData}
  />
);

interface SubRowProps {
  pl: number;
  lt: LabTest;
  children?: React.ReactNode;
  hasSubTests?: boolean;
  pushRight?: boolean;
  idx: number;
  gender: "1" | "2";
  dob: string;
  showMethods: boolean;
  showRanges: boolean;
  showUnit: boolean;
  showReading: boolean;
  showRangeFlag: boolean;
  showLabTestsWithoutReadingsInPrint?: boolean;
  type?: string;
}

export const SubRow = ({
  pl,
  lt,
  children = <></>,
  hasSubTests = false,
  pushRight = false,
  idx,
  gender,
  dob,
  showMethods,
  showRanges,
  showUnit,
  showReading,
  showRangeFlag,
  showLabTestsWithoutReadingsInPrint = false,
  type
}: SubRowProps): JSX.Element => {
  const commonStyles = {
    fontSize: scaleFont("0.33cm", 1.105),
    wordBreak: "break-all"
  };

  const rangeSymbol = advancedLabRangeIndicator(
    Number(removeCommasFromNumber(lt.formData.reading)),
    lt.ranges as unknown as AdvancedRange,
    dob,
    gender
  );

  const isRadiology = isRadiologyOrCytology(lt.type || type);

  return (
    <>
      {showSubRow(hasSubTests, lt, showLabTestsWithoutReadingsInPrint) && (
        <Box
          p="0.05cm"
          pl={pushRight ? "0.2cm" : "0.1cm"}
          borderLeft="1px solid #bbb"
          borderRight="1px solid #bbb"
          borderTop={!idx && 0}
          borderBottom="1px solid #bbb"
          style={{
            pageBreakInside: "avoid"
          }}
        >
          {isRadiology ? (
            <Box style={{ paddingLeft: `${pl}cm` }}>
              <Typography
                style={{
                  fontSize: scaleFont("0.33cm", 1.105),
                  paddingRight: "0.6cm",
                  ...(hasSubTests && { fontWeight: "bold" })
                }}
              >
                {lt.name}
              </Typography>
              {showReading && lt?.formData?.reading && (
                <Typography
                  component="div"
                  style={{
                    ...commonStyles,
                    whiteSpace: "pre-wrap",
                    paddingTop: "2px"
                  }}
                >
                  {lt.formData.reading.includes("<") ? (
                    <CustomEl data={lt.formData.reading} />
                  ) : (
                    lt.formData.reading
                  )}
                </Typography>
              )}
            </Box>
          ) : (
            <Box display="flex">
              <Box
                flexBasis={pushRight ? "5.8cm" : "5.9cm"}
                flexShrink="0.4"
                flexGrow="0.4"
                style={{ paddingLeft: `${pl}cm` }}
              >
                <Typography
                  style={{
                    fontSize: scaleFont("0.33cm", 1.105),
                    paddingRight: "0.6cm",
                    ...(hasSubTests && { fontWeight: "bold" })
                  }}
                >
                  {lt.name}
                </Typography>
              </Box>
              {showReading && (
                <Typography
                  style={{
                    ...commonStyles,
                    fontWeight: rangeSymbol ? 900 : 500,
                    marginRight: "0.5cm"
                  }}
                  flexBasis="4cm"
                  flexShrink="0.15"
                  flexGrow="0.15"
                >
                  {lt?.formData.reading || null}
                </Typography>
              )}
              {showRangeFlag && (
                <Typography
                  style={commonStyles}
                  flexBasis="1.5cm"
                  flexShrink="0.15"
                  flexGrow="0.15"
                >
                  {rangeSymbol.highSymbol || rangeSymbol.lowSymbol || null}
                </Typography>
              )}
              {showUnit && (
                <Typography
                  style={commonStyles}
                  flexBasis="1.8cm"
                  flexShrink="0.15"
                  flexGrow="0.15"
                >
                  {!(lt.subTests?.length > 0) && hasOwnProperty(lt, "unit") && lt.unit}
                </Typography>
              )}
              {showRanges && (
                <Box style={commonStyles} flexBasis="3.7cm" flexShrink="0.15" flexGrow="0.15">
                  <AdvancedLabRangeHandler range={lt.ranges} dob={dob} gender={Number(gender)} />
                </Box>
              )}
              {showMethods && (
                <Typography
                  sx={{
                    fontSize: scaleFont("0.25cm", 1),
                    wordBreak: "break-all"
                  }}
                  flexBasis="3cm"
                  flexShrink="0.15"
                  flexGrow="0.15"
                >
                  {lt.methods}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      <div>{children}</div>

      {lt.subTests?.map((sT, i) => (
        <SubRow
          key={sT.id}
          pl={pl + 0.5}
          lt={addDefaultFormData(sT)}
          dob={dob}
          idx={i}
          gender={gender}
          showMethods={showMethods}
          showRanges={showRanges}
          showUnit={showUnit}
          showReading={showReading}
          showRangeFlag={showRangeFlag}
          hasSubTests={Boolean(sT.subTests?.length)}
          type={sT.type || type}
        />
      ))}
    </>
  );
};

interface RowProps {
  item: LabTestRecordResultData;
  pushRight: boolean;
  gender: "1" | "2";
  showAdditionalLabData: boolean;
  showMethods: boolean;
  dob: string;
  showRanges: boolean;
  showUnit: boolean;
  showReading: boolean;
  showRangeFlag: boolean;
  showSampleCollectedBy: boolean;
  showLabTestsWithoutReadingsInPrint?: boolean;
  type?: string;
}

export const Row = ({
  item,
  pushRight,
  gender,
  showAdditionalLabData,
  showMethods,
  dob,
  showRanges,
  showUnit,
  showReading,
  showRangeFlag,
  showSampleCollectedBy,
  showLabTestsWithoutReadingsInPrint = false,
  type
}: RowProps): JSX.Element => {
  const showTestCategory = verifyShowTestCategory(item);

  return (
    <div>
      <Box
        sx={{
          paddingLeft: "0.2cm",
          paddingTop: "0.2cm",
          borderLeft: "1px solid #bbb",
          borderRight: "1px solid #bbb"
        }}
      >
        <TestNameWithSampleInfo
          test={{
            name: showTestCategory ? item.name : "",
            style: {
              fontSize: scaleFont("0.33cm", 1.105)
            }
          }}
          sampleInfo={{
            sampleType: item.sampleInfo?.sampleType
          }}
          collectedBy={{ name: showSampleCollectedBy ? item.collectedBy?.name : "" }}
        />
      </Box>

      <div>
        {item.labTests?.map((lt, i) => (
          <SubRow
            key={lt.id}
            pl={showTestCategory ? 0.5 : 0}
            lt={addDefaultFormData(lt)}
            hasSubTests={Boolean(lt.subTests?.length)}
            pushRight={pushRight}
            dob={dob}
            idx={i}
            gender={gender}
            showMethods={showMethods}
            showRanges={showRanges}
            showUnit={showUnit}
            showReading={showReading}
            showRangeFlag={showRangeFlag}
            showLabTestsWithoutReadingsInPrint={showLabTestsWithoutReadingsInPrint}
            type={lt.type || item.type || type}
          />
        ))}

        {showAdditionalLabData && item?.additionalTestData && (
          <div style={{ whiteSpace: "pre-wrap" }}>
            <CustomEl data={item?.additionalTestData} />
          </div>
        )}

        <div>
          {item?.interpretationTemplate && (
            <EditableTableView tableState={item?.interpretationTemplate} />
          )}
        </div>
      </div>
    </div>
  );
};

export const DataHeaders = ({
  headers = ["Test", "Result", "Unit", "Range"],
  showMethods,
  type,
  readingsPresent,
  showRangeFlag
}: {
  headers?: string[];
  showMethods: boolean;
  type: string;
  readingsPresent: boolean;
  showRangeFlag: boolean;
}): JSX.Element => {
  const printHeaders = [...headers];
  const isRadiology = isRadiologyOrCytology(type);

  if (showRangeFlag) {
    if (printHeaders.length <= 2) {
      printHeaders.push("Flag");
    } else {
      printHeaders.splice(2, 0, "Flag");
    }
  }
  if (showMethods) {
    printHeaders.push("Methods");
  }
  if (isRadiology) {
    printHeaders.splice(1, printHeaders.length);
  }

  const getFlexGrow = (index: number) => {
    if (isRadiology) return 1;
    return index === 0 ? 0.4 : 0.15;
  };

  return (
    <>
      {readingsPresent && (
        <Box p="0.1cm" display="flex" border="1px solid #bbb">
          {printHeaders.map((colHeader, i) => (
            <Typography
              key={colHeader}
              style={{
                flexGrow: getFlexGrow(i),
                flexShrink: i === 0 ? 0.4 : 0.15,
                flexBasis: isRadiology ? "100%" : LabHeadersFlexBasisLargeFont[colHeader] || "6cm",
                fontSize: scaleFont("0.27cm", 1.3),
                textTransform: "uppercase",
                fontWeight: 600
              }}
            >
              {colHeader}
            </Typography>
          ))}
        </Box>
      )}
    </>
  );
};

DataHeaders.defaultProps = {
  headers: ["Test", "Result", "Unit", "Range"]
};

interface CoreDataProps {
  labData: Record<string, any>;
  showAdditionalLabData: boolean;
  showMethods: boolean;
  categorySortOrder: Record<string, any>;
  showRanges: boolean;
  showUnit: boolean;
  showReading: boolean;
  readingsPresent: boolean;
  showRangeFlag: boolean;
  showSampleCollectedBy: boolean;
  showLabTestsWithoutReadingsInPrint?: boolean;
}

export const CoreData = ({
  labData,
  showAdditionalLabData,
  showMethods,
  categorySortOrder,
  showRanges,
  showUnit,
  showReading,
  readingsPresent,
  showRangeFlag,
  showSampleCollectedBy,
  showLabTestsWithoutReadingsInPrint
}: CoreDataProps): JSX.Element => {
  const { data } = labData.results;
  const { gender, dob } = labData.client;

  const getHeaders = (item: string) => {
    let headers = ["Test", "Result", "Unit"];
    if (["molecular tests", "culture tests"].includes(item?.toLowerCase())) {
      headers = ["Test", "Result"];
    }
    if (showRanges && showUnit) {
      headers = ["Test", "Result", "Unit", "Range"];
    }
    if (showRanges && !showUnit) {
      headers = ["Test", "Result", "Range"];
    }
    if (!showRanges && showUnit) {
      headers = ["Test", "Result", "Unit"];
    }
    if (!showReading) {
      headers = ["Test"];
    }
    return headers;
  };

  const showRowColumn = !isRadiologyOrCytology(labData.type);

  const sortedCategories = sortCategoryKeys(
    Object.keys(data),
    categorySortOrder?.map((item) => item.category) || []
  );

  return (
    <div>
      {sortedCategories.map(
        (item, i) =>
          showCategory(
            data[item],
            labData?.status === LabTestStatusTypes.ORDERED || showLabTestsWithoutReadingsInPrint
          ) && (
            <div key={item} style={i !== 0 ? { pageBreakBefore: "always" } : {}} className="page">
              {!EXCLUDE_GROUP_CATEGORY.includes(item) && (
                <Typography
                  style={{
                    textTransform: "uppercase",
                    fontWeight: "bold",
                    paddingLeft: "0.2cm",
                    paddingTop: "0.2cm",
                    width: "100%",
                    textDecoration: "underline",
                    fontSize: scaleFont("0.33cm", 1.192)
                  }}
                >
                  {item}
                </Typography>
              )}

              {labData?.status !== LabTestStatusTypes.ORDERED && (
                <DataHeaders
                  showMethods={showMethods}
                  type={labData.type}
                  headers={getHeaders(item)}
                  readingsPresent={readingsPresent}
                  showRangeFlag={showRangeFlag}
                />
              )}

              {data[item].map((test: LabTestRecordResultData) => (
                <Row
                  key={test.name}
                  item={test}
                  pushRight={!EXCLUDE_GROUP_CATEGORY.includes(item)}
                  gender={gender}
                  dob={dob}
                  showAdditionalLabData={showAdditionalLabData}
                  showMethods={showRowColumn && showMethods}
                  showRanges={showRowColumn && showRanges}
                  showUnit={showRowColumn && showUnit}
                  showReading={showReading}
                  showRangeFlag={showRangeFlag}
                  showSampleCollectedBy={showSampleCollectedBy}
                  showLabTestsWithoutReadingsInPrint={showLabTestsWithoutReadingsInPrint}
                  type={test.type || labData.type}
                />
              ))}
            </div>
          )
      )}
    </div>
  );
};

export const DataComment = ({ comment }: { comment: string }): JSX.Element => (
  <Box mt="16px">
    <CustomEl data={comment} />
  </Box>
);

interface LabPrintCoreDataProps {
  labData: Record<string, any>;
  showAdditionalLabData: boolean;
  showMethods: boolean;
  categorySortOrder: Record<string, any>;
  showRanges: boolean;
  showUnit: boolean;
  showReading: boolean;
  showRangeFlag: boolean;
  showSampleCollectedBy: boolean;
}

const LabPrintCoreData = ({
  labData,
  showAdditionalLabData,
  showMethods,
  categorySortOrder,
  showRanges,
  showUnit,
  showReading,
  showRangeFlag,
  showSampleCollectedBy
}: LabPrintCoreDataProps): React.ReactNode => {
  const { showLabTestsWithoutReadingsInPrint } = useShowLabTestsWithoutReadingsInPrint();
  const readingContains = readingPresent(
    labData,
    labData?.status === LabTestStatusTypes.ORDERED || showLabTestsWithoutReadingsInPrint
  );

  return (
    <div>
      <CoreData
        labData={labData}
        showAdditionalLabData={showAdditionalLabData}
        showMethods={showMethods}
        categorySortOrder={categorySortOrder}
        showRanges={showRanges}
        showUnit={showUnit}
        showReading={showReading}
        readingsPresent={readingContains}
        showRangeFlag={showRangeFlag}
        showSampleCollectedBy={showSampleCollectedBy}
        showLabTestsWithoutReadingsInPrint={showLabTestsWithoutReadingsInPrint}
      />

      <Box mt="8px">
        {labData.results.showComment && labData.results.comment && (
          <DataComment comment={labData.results.comment} />
        )}
      </Box>
    </div>
  );
};

export default LabPrintCoreData;
